import { supabase } from "@/lib/supabase";

export async function getOrCreateCart(userId: string) {
  const { data: existing } = await supabase
    .from("carts")
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();
  if (existing) return existing.id as string;
  const { data: created, error } = await supabase
    .from("carts")
    .insert({ user_id: userId })
    .select("id")
    .single();
  if (error) throw error;
  return created.id as string;
}

export async function addItemToCart(userId: string, productId: string, qty: number) {
  const cartId = await getOrCreateCart(userId);
  // If item exists, update qty; else insert
  const { data: existing } = await supabase
    .from("cart_items")
    .select("id, qty")
    .eq("cart_id", cartId)
    .eq("product_id", productId)
    .maybeSingle();
  if (existing) {
    const { error } = await supabase
      .from("cart_items")
      .update({ qty: existing.qty + qty })
      .eq("id", existing.id);
    if (error) throw error;
    return;
  }
  const { error } = await supabase
    .from("cart_items")
    .insert({ cart_id: cartId, product_id: productId, qty });
  if (error) throw error;
}

export async function updateCartItemQty(itemId: string, qty: number) {
  const { error } = await supabase.from("cart_items").update({ qty }).eq("id", itemId);
  if (error) throw error;
}

export async function removeCartItem(itemId: string) {
  const { error } = await supabase.from("cart_items").delete().eq("id", itemId);
  if (error) throw error;
}

export async function loadCart(userId: string) {
  const cartId = await getOrCreateCart(userId);
  // Load items with product info
  const { data, error } = await supabase
    .from("cart_items")
    .select("id, qty, product:products(id, title, price_cents, currency)")
    .eq("cart_id", cartId);
  if (error) throw error;
  return { cartId, items: data ?? [] } as { cartId: string; items: Array<{ id: string; qty: number; product: { id: string; title: string; price_cents: number; currency: string } }> };
}

export async function checkoutCart(userId: string) {
  const { cartId, items } = await loadCart(userId);
  const total_cents = items.reduce((sum, it) => sum + it.qty * (it.product?.price_cents ?? 0), 0);
  const currency = items[0]?.product?.currency ?? "BSD";
  // Create order
  const { data: order, error } = await supabase
    .from("orders")
    .insert({ buyer_id: userId, total_cents, currency, status: "pending" })
    .select("id")
    .single();
  if (error) throw error;
  const orderId = order.id as string;
  // Insert order_items
  const payload = items.map((it) => ({
    order_id: orderId,
    product_id: it.product.id,
    shop_id: undefined as unknown as string, // will be filled by server-side rule; or we can fetch it
    qty: it.qty,
    price_cents: it.product.price_cents,
  }));
  // Fetch shop_id for each product
  for (const row of payload) {
    const { data: prod, error: perr } = await supabase
      .from("products")
      .select("shop_id")
      .eq("id", row.product_id)
      .single();
    if (perr) throw perr;
    row.shop_id = prod.shop_id;
  }
  const { error: oiErr } = await supabase.from("order_items").insert(payload);
  if (oiErr) throw oiErr;
  // Clear cart
  const { error: clearErr } = await supabase.from("cart_items").delete().eq("cart_id", cartId);
  if (clearErr) throw clearErr;
  return orderId;
}
